/* eslint-disable id-length */
import {
  string, shape, number, bool, customType, arrayOf, id
} from '@thd-nucleus/data-sources';

interface IRequestModel {
  requestContext: object;
  responseModel: object;
  isMutation?: boolean;
}

interface IRequestModels {
  [queryName: string]: IRequestModel;
}

// Get List of Gift cards on my account page
const paginationInfo = {
  pageNumber: number(),
  totalPages: number(),
  totalNumberOfRecords: number(),
  pageSize: number()
};

const paymentCard = {
  nickName: string(),
  lastModifiedDate: string(),
  id: string(),
  type: string(),
  level: string(),
  addedBy: string(),
  imagePath: string(),
  actions: shape({
    edit: bool(),
    viewPurchases: bool(),
    delete: bool(),
    reload: bool(),
    onlineTransactable: bool(),
    isLegacyCard: bool()
  }),
  cardDetails: shape({
    cardNumberLast4: string(),
    cardStatus: string(),
    cardNumber12Digits: string()
  })
};

const transactableCard = {
  nickName: string(),
  lastModifiedDate: string(),
  id: string(),
  imagePath: string(),
  availableBalance: number(),
  cardDetails: shape({
    cardNumberLast4: string()
  })
};

const giftCardsResponseModel = {
  paginationInfo,
  paymentCards: arrayOf(shape(paymentCard))
};

// Get Gift Card Barcode for individual card scan-to-pay
const getGiftCardBarcodeResponseModel = {
  paymentCards: arrayOf(
    shape({
      id: string(),
      encryptedGiftCard: shape({
        cipherText: string(),
        iv: string()
      })
    })
  ),
  otpVerificationResult: shape({
    otpVerified: string(),
    remainingCount: string(),
    encryptedPayload: string()
  })
};

const getGiftCardBarcodeRequestContext = {
  paymentIds: arrayOf(string()).isRequired(),
  otpVerificationInput: customType('OTPVerificationInput').shape({
    email: string(),
    encryptedPayload: string(),
    otp: string()
  }),
  giftCardCipherKeyInput: customType('GiftCardCipherKeyInput').shape({
    x: string(),
    y: string()
  })
};

const giftCardsRequestContext = {
  paginate: customType('V2PaginationInput').shape({ pageSize: string(), pageNumber: string() }),
  sort: customType('V2SortInput').shape({ sortBy: string(), asc: bool() })
};

// Get List of Gift cards balance on my account page
const getGiftCardBalanceResponseModel = {
  paymentCards: arrayOf(
    shape({
      id: string(),
      availableBalance: string()
    })
  )
};

const getGiftCardBalanceRequestContext = {
  paymentIds: arrayOf(id()).isRequired(),
  recaptcha: string()
};

// Add Gift Card

const addGiftCardResponseModel = {
  paymentCards: {
    saveToAccountStatus: bool(),
    id: string()
  }
};

const addGiftCardRequestContext = {
  giftCard: customType('V2GiftCardInput').shape({
    paymentValue: string(),
    pin: string(),
    nickName: string(),
    imagePath: string(),
    recaptchaToken: string()
  })
};

// Delete Gift Card

const deleteGiftCardResponseModel = {
  paymentCards: {
    saveToAccountStatus: bool()
  }
};

const deleteGiftCardRequestContext = {
  giftCard: customType('V2GiftCardInput').shape({ id: string() })
};

// Update Gift Card
const updateGiftCardResponseModel = {
  paymentCards: {
    saveToAccountStatus: bool(),
    id: string()
  }
};

const updateGiftCardRequestContext = {
  giftCard: customType('V2GiftCardInput').shape({
    id: string(),
    paymentValue: string(),
    pin: string(),
    imagePath: string(),
    nickName: string(),
    actions: shape({
      onlineTransactable: bool()
    })
  })
};

// Get Transactable Gift Cards for Scan to Pay on mobile

const transactableGiftCardsResponseModel = {
  paginationInfo,
  paymentCards: arrayOf(shape(transactableCard))
};

const transactableGiftCardsRequestContext = giftCardsRequestContext;

export const useRequestModels: IRequestModels = {
  getGiftCardBarcode: {
    requestContext: getGiftCardBarcodeRequestContext,
    responseModel: getGiftCardBarcodeResponseModel
  },
  giftCards: {
    requestContext: giftCardsRequestContext,
    responseModel: giftCardsResponseModel
  },
  giftCardBalancesByPaymentIds: {
    requestContext: getGiftCardBalanceRequestContext,
    responseModel: getGiftCardBalanceResponseModel
  },
  addGiftCardV2: {
    requestContext: addGiftCardRequestContext,
    responseModel: addGiftCardResponseModel,
    isMutation: true
  },
  deleteGiftCardV2: {
    requestContext: deleteGiftCardRequestContext,
    responseModel: deleteGiftCardResponseModel,
    isMutation: true
  },
  updateGiftCardV2: {
    requestContext: updateGiftCardRequestContext,
    responseModel: updateGiftCardResponseModel,
    isMutation: true
  },
  transactableGiftCards: {
    requestContext: transactableGiftCardsRequestContext,
    responseModel: transactableGiftCardsResponseModel
  }
};
